/**
 * Persistence layer for the EntitySelector's `entityOverride` slice.
 * Mirrors portalnew's behaviour where redux-persist whitelists the
 * `user` reducer so the selected merchant/vendor survives F5. We
 * persist to localStorage (not session) to match prod — closing and
 * reopening the browser keeps the scope active.
 *
 * Failure modes: localStorage can throw in private-browsing / SSR /
 * quota-exceeded scenarios. Every call is wrapped in a try/catch and
 * returns a safe fallback so the slice never crashes the app boot.
 */

import type { EntityOverride } from './types';

const STORAGE_KEY = 'om.entityOverride';
const OWNER_KEY = 'om.entityOverride.owner';

/**
 * Validate a parsed payload before trusting it as an EntityOverride. A
 * corrupted / hand-edited localStorage entry (unknown `type`, missing id) must
 * not flow into the auth slice and scope queries to a bogus entity. Lenient by
 * design: admin's id is null, so only merchant/vendor require a string id.
 */
function isValidEntityOverride(value: unknown): value is EntityOverride {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  if (o.type !== 'merchant' && o.type !== 'vendor' && o.type !== 'admin') return false;
  if ((o.type === 'merchant' || o.type === 'vendor') && typeof o.id !== 'string') return false;
  return true;
}

export function readEntityOverride(): EntityOverride | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidEntityOverride(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The user id that owns the persisted override. Lets the auth slice clear a
 * stale scope when a *different* user signs in on a shared browser, so User B
 * never inherits User A's "view as" (see authSlice.userAuthenticated).
 */
export function readEntityOverrideOwner(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(OWNER_KEY);
  } catch {
    return null;
  }
}

export function writeEntityOverride(
  value: EntityOverride | null,
  ownerId?: string | null,
): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(OWNER_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    if (ownerId != null) window.localStorage.setItem(OWNER_KEY, ownerId);
  } catch {
    // Best-effort — we don't surface a UI error if persistence fails.
  }
}
